import { Box, Center, Heading } from '@chakra-ui/react';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import RenderEventSchema from '@lib/renderJsonLd';
import useOpenGraphLocale from '@lib/hooks/useOpenGraphLocale';
import { EventType } from '@lib/types';
import { asyncPost } from '@lib/fetch';

import EventLayout from '../../layouts/EventLayout';
import { EVENT_FALLBACK_COVER_PHOTO_URL, ORIGIN } from '../../constants';
import { HeroSection, InfoSection } from '../../containers/EventPage';

type Props = {
  event?: EventType;
};

const EventPage = ({ event }: Props) => {
  const { t } = useTranslation(['common']);
  const openGraphLocale = useOpenGraphLocale();

  const router = useRouter();
  const { locale } = router;

  if (!event) {
    // TODO: add more hint when 404 not found or event is deleted
    return (
      <EventLayout>
        <Box height="40vh">
          <Center bg="#f7f7f7" height="100%">
            <Heading fontWeight="500" fontSize="30px" lineHeight="56px">
              {t('common:Page not exist')}
            </Heading>
          </Center>
        </Box>
      </EventLayout>
    );
  }

  const coverPhoto = event.coverPhoto || EVENT_FALLBACK_COVER_PHOTO_URL;

  return (
    <EventLayout>
      <NextSeo
        title={event.name}
        description={event.description.replace(/(<([^>]+)>)/gi, '')}
        openGraph={{
          type: 'website',
          locale: openGraphLocale,
          url: `https://nutshell.live/events/${event.id}`,
          title: event.name,
          description: event.description.replace(/(<([^>]+)>)/gi, ''),
          images: [
            {
              url: coverPhoto,
              alt: event.name,
            },
          ],
        }}
      />
      <RenderEventSchema
        event={event}
        locale={locale}
        coverPhoto={coverPhoto}
      />

      <HeroSection event={event} />
      <InfoSection event={event} />
    </EventLayout>
  );
};

// ref: https://vercel.com/docs/next.js/incremental-static-regeneration#generating-paths
export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking', // official preferred
  };
}

// ref: https://vercel.com/docs/next.js/incremental-static-regeneration#fetching-data
export async function getStaticProps({
  params,
}: {
  params: { eventId: string };
}) {
  const eventId = params.eventId;

  try {
    const result = await asyncPost({
      url: `${ORIGIN}/api/query/getEvent`,
      body: { eventId },
    });

    return {
      props: {
        event: result,
      },
      revalidate: 1,
    };
  } catch (err) {
    return {
      props: {
        event: null,
      },
      revalidate: 1,
    };
  }
}

export default EventPage;
